import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import type { ToastActionElement, ToastProps } from '@/components/ui/Toast';

/**
 * A module-scoped store, not React context — `toast(...)` needs to be
 * callable from a plain event handler or a mutation's `onError` anywhere in
 * the app (see PatientRecords.tsx/ErasureRequestsTab.tsx's `window.alert()`
 * replacements) without every caller needing to be inside a Provider's
 * subtree or thread a hook return value down through props. `<Toaster/>`
 * (mounted once in App.tsx) is the only consumer of `useToast()`'s live
 * list; everyone else just calls `toast()`.
 */

export interface ToastOptions {
  title?: ReactNode;
  description?: ReactNode;
  variant?: ToastProps['variant'];
  action?: ToastActionElement;
  /** ms before auto-dismiss. Defaults to 5000; pass `Infinity` for a toast that only dismisses via its close button or action. */
  duration?: number;
}

interface ToastRecord extends ToastOptions {
  id: string;
  open: boolean;
}

const DEFAULT_DURATION = 5000;
let toasts: ToastRecord[] = [];
const listeners = new Set<(toasts: ToastRecord[]) => void>();

function emit() {
  listeners.forEach((listener) => listener(toasts));
}

function dismiss(id: string) {
  toasts = toasts.map((t) => (t.id === id ? { ...t, open: false } : t));
  emit();
  // Radix needs a beat to run its close animation before the node is
  // actually removed from the list, or the toast just vanishes mid-swipe.
  setTimeout(() => {
    toasts = toasts.filter((t) => t.id !== id);
    emit();
  }, 200);
}

export function toast(options: ToastOptions) {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  toasts = [...toasts, { ...options, id, open: true }];
  emit();
  const duration = options.duration ?? DEFAULT_DURATION;
  if (Number.isFinite(duration)) {
    setTimeout(() => dismiss(id), duration);
  }
  return { id, dismiss: () => dismiss(id) };
}

export function useToast() {
  const [state, setState] = useState(toasts);
  useEffect(() => {
    listeners.add(setState);
    return () => { listeners.delete(setState); };
  }, []);
  return { toasts: state, toast, dismiss };
}
